import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useRef, useState, type ReactNode } from 'react';
import {
  ArrowLeftRight,
  Biohazard,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  Clock,
  ExternalLink,
  Lock,
  LockOpen,
  LogOut,
  Receipt,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Stethoscope,
  Timer,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type { BedBoardCell } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn, dateOnly, dateTime, initials, money, relative } from '@/lib/utils';
import { ActionMenuItem, ActionMenuSeparator, Button, Tooltip, menuContentClass, statusLabel } from '@/components/ui/primitives';
import { boardStatusOf, dischargeDue, patientAge, stayDay, type BoardStatus, type DischargeDue } from './bed-status';
import { STATUS_LOOK, ageText, bedClassLabel, payerIcon } from './bed-board-look';
import { BedArt, SexGlyph } from './bed-board-art';

export interface BedAbilities {
  admit: boolean;
  readAdmission: boolean;
  transfer: boolean;
  discharge: boolean;
  manageBeds: boolean;
  housekeeping: boolean;
  bill: boolean;
}

export type BedStatusTarget = 'vacant' | 'cleaning' | 'reserved' | 'blocked';

export interface BedHandlers {
  openAdmission: (cell: BedBoardCell) => void;
  admit: (cell: BedBoardCell) => void;
  transfer: (cell: BedBoardCell) => void;
  discharge: (cell: BedBoardCell) => void;
  bill: (cell: BedBoardCell) => void;
  setStatus: (cell: BedBoardCell, target: BedStatusTarget | null) => void;
  housekeeping: () => void;
}

interface Flag {
  id: string;
  icon: LucideIcon;
  label: string;
  tone: string;
}

interface MenuAction {
  id: string;
  icon: ReactNode;
  label: string;
  hint?: string;
  destructive?: boolean;
  run: () => void;
}

const STAGGER_MS = 28;
const STAGGER_CAP = 18;

const DISCHARGE_FLAG: Record<DischargeDue, { icon: LucideIcon; tone: string }> = {
  today: { icon: CalendarCheck, tone: 'bg-success/10 text-success' },
  tomorrow: { icon: CalendarClock, tone: 'bg-info/10 text-info' },
  overdue: { icon: CalendarX, tone: 'bg-warning/15 text-warning' },
};

function dischargeLabel(due: DischargeDue, expectedAt: string): string {
  if (due === 'today') return tr('ipd', 'discharge_expected_today');
  if (due === 'tomorrow') return tr('ipd', 'discharge_expected_tomorrow');
  return tr('ipd', 'past_expected_discharge_x', { date: dateOnly(expectedAt) });
}

function patientFlags(cell: BedBoardCell, due: DischargeDue | null): Flag[] {
  const flags: Flag[] = [];
  if (cell.allergyCount) {
    flags.push({
      id: 'allergy',
      icon: ShieldAlert,
      label: tr('ipd', 'x_allergies_recorded', { count: cell.allergyCount }),
      tone: 'bg-destructive/10 text-destructive',
    });
  }
  if (cell.payerName) {
    flags.push({
      id: 'payer',
      icon: payerIcon(cell.payerKind),
      label: tr('ipd', 'covered_by_x', { payer: cell.payerName }),
      tone: 'bg-primary/10 text-primary',
    });
  }
  if (due && cell.expectedDischargeAt) {
    flags.push({ id: 'discharge', ...DISCHARGE_FLAG[due], label: dischargeLabel(due, cell.expectedDischargeAt) });
  }
  return flags;
}

function statusAction(cell: BedBoardCell, handlers: BedHandlers, target: BedStatusTarget, label: string, icon: ReactNode): MenuAction {
  return { id: `status-${target}`, icon, label, run: () => handlers.setStatus(cell, target) };
}

function bedActions(cell: BedBoardCell, status: BoardStatus, can: BedAbilities, handlers: BedHandlers): MenuAction[] {
  const actions: MenuAction[] = [];
  const admitHere: MenuAction = {
    id: 'admit',
    icon: <UserPlus />,
    label: tr('ipd', 'admit_a_patient_here'),
    hint: tr('ipd', 'choose_bed_x_on_the_admission_form', { bedCode: cell.bedCode }),
    run: () => handlers.admit(cell),
  };

  if (status === 'occupied' && cell.admissionId) {
    if (can.readAdmission) actions.push({ id: 'open', icon: <ExternalLink />, label: tr('ipd', 'open_admission'), run: () => handlers.openAdmission(cell) });
    if (can.transfer) actions.push({ id: 'transfer', icon: <ArrowLeftRight />, label: tr('ipd', 'transfer_bed'), run: () => handlers.transfer(cell) });
    if (can.bill) actions.push({ id: 'bill', icon: <Receipt />, label: tr('ipd', 'statement'), run: () => handlers.bill(cell) });
    if (can.discharge) actions.push({ id: 'discharge', icon: <LogOut />, label: tr('ipd', 'discharge'), run: () => handlers.discharge(cell) });
    return actions;
  }

  if (status === 'vacant') {
    if (can.admit) actions.push(admitHere);
    if (can.manageBeds) {
      actions.push(statusAction(cell, handlers, 'reserved', tr('ipd', 'reserve_bed'), <Clock />));
      actions.push(statusAction(cell, handlers, 'cleaning', tr('ipd', 'send_to_cleaning'), <Sparkles />));
      actions.push(statusAction(cell, handlers, 'blocked', tr('ipd', 'block_bed'), <Lock />));
    }
  }
  if (status === 'cleaning') {
    if (can.manageBeds) actions.push(statusAction(cell, handlers, 'vacant', tr('ipd', 'mark_cleaned_and_available'), <Sparkles />));
    if (can.housekeeping) actions.push({ id: 'housekeeping', icon: <ExternalLink />, label: tr('ipd', 'open_housekeeping'), run: handlers.housekeeping });
    if (can.manageBeds) actions.push(statusAction(cell, handlers, 'blocked', tr('ipd', 'block_bed'), <Lock />));
  }
  if (status === 'reserved') {
    if (can.manageBeds) {
      actions.push(statusAction(cell, handlers, 'vacant', tr('ipd', 'release_reservation'), <LockOpen />));
      actions.push(statusAction(cell, handlers, 'blocked', tr('ipd', 'block_bed'), <Lock />));
    }
  }
  if (status === 'blocked' && can.manageBeds) {
    actions.push(statusAction(cell, handlers, 'vacant', tr('ipd', 'unblock_bed'), <LockOpen />));
  }
  if (can.manageBeds && cell.admissionId === null) {
    actions.push({ id: 'change', icon: <RefreshCw />, label: tr('ipd', 'change_status'), run: () => handlers.setStatus(cell, null) });
  }
  return actions;
}

function sinceLabel(status: BoardStatus, changedAt: string): string | null {
  const when = relative(changedAt);
  if (status === 'cleaning') return tr('ipd', 'cleaning_started_x', { when });
  if (status === 'reserved') return tr('ipd', 'reserved_x', { when });
  if (status === 'blocked') return tr('ipd', 'blocked_x', { when });
  return null;
}

function FlagIcon({ flag }: { flag: Flag }) {
  const Icon = flag.icon;
  return (
    <Tooltip content={flag.label}>
      <span role="img" aria-label={flag.label} className={cn('relative z-[2] grid h-6 w-6 place-items-center rounded-md', flag.tone)}>
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Tooltip>
  );
}

export function BedCard({
  cell,
  now,
  timeZone,
  index,
  flashing,
  reduced,
  can,
  handlers,
}: {
  cell: BedBoardCell;
  now: Date;
  timeZone: string;
  index: number;
  flashing: boolean;
  reduced: boolean;
  can: BedAbilities;
  handlers: BedHandlers;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const touched = useRef(false);
  const openedBeforeTap = useRef(false);
  const status = boardStatusOf(cell.status);
  const look = STATUS_LOOK[status];
  const StatusIcon = look.icon;
  const occupied = status === 'occupied' && cell.admissionId !== null;
  const due = occupied ? dischargeDue(cell.expectedDischargeAt, now, timeZone) : null;
  const flags = occupied ? patientFlags(cell, due) : [];
  const day = occupied && cell.admittedAt ? stayDay(cell.admittedAt, now, timeZone) : null;
  const age = occupied ? ageText(patientAge(cell.patientDob, cell.patientAgeYears, now)) : null;
  const sexLabel = cell.patientSex ? statusLabel(cell.patientSex) : null;
  const actions = bedActions(cell, status, can, handlers);
  const since = sinceLabel(status, cell.statusChangedAt);
  const isolation = cell.bedClass === 'isolation';

  const summary = [
    tr('ipd', 'bed_x', { bedCode: cell.bedCode }),
    statusLabel(cell.status),
    bedClassLabel(cell.bedClass),
    cell.patientName,
    cell.mrn,
    day !== null ? tr('ipd', 'day_x', { count: day }) : null,
    ...flags.map((flag) => flag.label),
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className="relative h-full duration-500 fill-mode-backwards motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2"
      style={{ animationDelay: `${Math.min(index, STAGGER_CAP) * STAGGER_MS}ms` }}
    >
      {flashing && !reduced && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 z-[3] rounded-xl border-2 duration-1000 ease-out fill-mode-forwards animate-out fade-out-0 zoom-out-[1.06]',
            look.flash,
          )}
        />
      )}
      <article
        className={cn(
          'group relative isolate flex h-full min-h-[11.5rem] flex-col overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm',
          'transition-[transform,box-shadow,border-color] duration-300 ease-out hover:shadow-lg motion-safe:hover:-translate-y-0.5 motion-safe:focus-within:-translate-y-0.5',
          look.border,
          look.glow,
          flashing && cn('ring-2 ring-offset-2 ring-offset-background', look.ring),
        )}
      >
        <span aria-hidden className={cn('absolute inset-y-0 start-0 w-1 transition-colors duration-500', look.edge)} />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute -end-12 -top-12 h-28 w-28 rounded-full opacity-70 blur-2xl transition-[opacity,background-color] duration-500 group-hover:opacity-100',
            look.blob,
          )}
        />

        <DropdownMenu.Root modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label={summary}
              onPointerDown={(event) => {
                touched.current = event.pointerType !== 'mouse';
                if (!touched.current) return;
                openedBeforeTap.current = menuOpen;
                event.preventDefault();
              }}
              onClick={(event) => {
                if (event.detail > 0 && touched.current && !openedBeforeTap.current) setMenuOpen(true);
              }}
              className="absolute inset-0 z-[1] cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="start" sideOffset={6} collisionPadding={8} className={cn(menuContentClass, 'w-64')}>
              <div className="flex items-center gap-2.5 px-2.5 pb-2 pt-1.5">
                <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset', look.tile)}>
                  <StatusIcon className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{tr('ipd', 'bed_x', { bedCode: cell.bedCode })}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {cell.wardName} · {tr('ipd', 'room_x', { code: cell.roomCode })} · {statusLabel(cell.status)}
                  </p>
                </div>
              </div>
              {cell.patientName && (
                <p className="truncate px-2.5 pb-2 text-xs text-muted-foreground">
                  {cell.patientName}
                  {cell.admissionNumber ? ` · ${cell.admissionNumber}` : ''}
                </p>
              )}
              <ActionMenuSeparator />
              {actions.length === 0 ? (
                <p className="px-2.5 py-2 text-xs text-muted-foreground">{tr('ipd', 'nothing_here_your_role_can_change')}</p>
              ) : (
                actions.map((action) => (
                  <ActionMenuItem key={action.id} icon={action.icon} hint={action.hint} destructive={action.destructive} onSelect={action.run}>
                    {action.label}
                  </ActionMenuItem>
                ))
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <header className="flex items-start gap-3 p-3 ps-4">
          <span
            className={cn(
              'grid h-11 w-11 shrink-0 place-items-center rounded-xl ring-1 ring-inset transition-colors duration-500',
              look.tile,
            )}
          >
            <StatusIcon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-base font-semibold leading-none tracking-tight">{cell.bedCode}</span>
              <span className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-muted/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-muted-foreground">
                {isolation && <Biohazard className="h-3 w-3" aria-hidden />}
                {bedClassLabel(cell.bedClass)}
              </span>
            </div>
            <p className={cn('mt-1.5 flex items-center gap-1.5 text-xs font-medium transition-colors duration-500', look.text)}>
              <span className={cn('h-1.5 w-1.5 rounded-full', look.edge)} aria-hidden />
              {statusLabel(cell.status)}
            </p>
          </div>
          {day !== null && cell.admittedAt && (
            <Tooltip content={tr('ipd', 'admitted_x', { when: dateTime(cell.admittedAt) })}>
              <span className="relative z-[2] shrink-0 rounded-full bg-info/10 px-2 py-0.5 text-[11px] font-semibold text-info tabular">
                {tr('ipd', 'day_x', { count: day })}
              </span>
            </Tooltip>
          )}
        </header>

        {occupied ? (
          <>
            <div className="flex flex-1 flex-col gap-2 px-4 pb-3">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-info/25 to-info/5 text-sm font-semibold text-info ring-2 ring-card">
                  {initials(cell.patientName ?? '')}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold leading-tight">{cell.patientName}</p>
                  <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="truncate font-mono">{cell.mrn}</span>
                    {(sexLabel || age) && <span aria-hidden>·</span>}
                    {cell.patientSex && sexLabel && <SexGlyph sex={cell.patientSex} label={sexLabel} />}
                    {age && <span className="shrink-0 tabular">{age}</span>}
                  </p>
                </div>
              </div>
              {cell.consultantName && (
                <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                  <Stethoscope className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="sr-only">{tr('ipd', 'attending_doctor')}</span>
                  <span className="truncate">{cell.consultantName}</span>
                </p>
              )}
              {cell.admittedAt && (
                <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate">{tr('ipd', 'admitted_x', { when: dateTime(cell.admittedAt) })}</span>
                </p>
              )}
            </div>
            {(flags.length > 0 || (cell.balanceMinor !== null && cell.balanceMinor !== 0)) && (
              <footer className="mt-auto flex min-h-[2.5rem] items-center justify-between gap-2 border-t border-border/60 bg-muted/30 px-4 py-1.5">
                <div className="flex items-center gap-1">
                  {flags.map((flag) => (
                    <FlagIcon key={flag.id} flag={flag} />
                  ))}
                </div>
                {cell.balanceMinor !== null && cell.balanceMinor !== 0 && (
                  <span className={cn('truncate text-xs font-semibold tabular', cell.balanceMinor < 0 ? 'text-destructive' : 'text-success')}>
                    {tr('ipd', cell.balanceMinor < 0 ? 'owes_x' : 'credit_x', { amount: money(Math.abs(cell.balanceMinor)) })}
                  </span>
                )}
              </footer>
            )}
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-4 pb-4 text-center">
            <BedArt className={cn('transition-colors duration-500', look.text)} />
            {status === 'vacant' ? (
              <>
                <p className="text-sm font-semibold">{tr('ipd', 'available')}</p>
                <p className="text-xs text-muted-foreground">{tr('ipd', 'ready_for_a_patient')}</p>
                {can.admit && (
                  <Button
                    size="xs"
                    variant="outline"
                    className="relative z-[2] mt-1 border-success/40 text-success hover:bg-success/10 hover:text-success"
                    onClick={() => handlers.admit(cell)}
                  >
                    <UserPlus aria-hidden />
                    {tr('ipd', 'admit')}
                  </Button>
                )}
              </>
            ) : (
              <>
                {since && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Timer className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {since}
                  </p>
                )}
                {cell.statusReason && <p className="line-clamp-2 text-xs italic text-muted-foreground">{cell.statusReason}</p>}
              </>
            )}
          </div>
        )}
      </article>
    </div>
  );
}
